import React from "react";
import styled from "@emotion/styled";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";

import {
  Grid,
  Divider as MuiDivider,
  Typography as MuiTypography,
} from "@mui/material";
import { spacing } from "@mui/system";
import { green, red } from "@mui/material/colors";
import useAuth from "../../../hooks/useAuth";

// Firebase v8 RTDB (compat). Assumes your app already initializes Firebase elsewhere.
import { database } from "../../../config/firebaseConfig";

import Actions from "./Actions";
import BarChart from "./BarChart";
import LineChart from "./LineChart";
import DoughnutChart from "./DoughnutChart";
import Stats from "./Stats";
import Table from "./Table";

const Divider = styled(MuiDivider)(spacing);

const Typography = styled(MuiTypography)(spacing);

function safeNum(n) {
  const x = Number(n);
  return Number.isFinite(x) ? x : 0;
}

function summarizeReport(report) {
  const stats = Array.isArray(report?.locationStats)
    ? report.locationStats
    : [];
  const items = stats.reduce((sum, s) => sum + safeNum(s?.totalItems), 0);
  const tests = stats.reduce((sum, s) => sum + safeNum(s?.totalTests), 0);
  const failures = stats.reduce((sum, s) => sum + safeNum(s?.failureCount), 0);
  const passes = Math.max(items - failures, 0);
  const passRate = items > 0 ? passes / items : 0;
  return { items, tests, failures, passes, passRate };
}

function summarizeReportsWindow(reportsWindow) {
  const n = reportsWindow.length;
  const totals = reportsWindow.reduce(
    (acc, r) => {
      const s = summarizeReport(r);
      acc.items += s.items;
      acc.tests += s.tests;
      acc.failures += s.failures;
      return acc;
    },
    { items: 0, tests: 0, failures: 0 }
  );
  const passes = Math.max(totals.items - totals.failures, 0);
  const passRate = totals.items > 0 ? passes / totals.items : 0;
  const avg = {
    items: n > 0 ? totals.items / n : 0,
    tests: n > 0 ? totals.tests / n : 0,
    failures: n > 0 ? totals.failures / n : 0,
    passRate,
  };
  return { totals, avg };
}

function shortDateLabel(dateStr) {
  // dateStr is typically like "1/25/2026" in your export
  if (!dateStr) return "";
  const parts = String(dateStr).split("/");
  if (parts.length >= 2) return `${parts[0]}/${parts[1]}`;
  return String(dateStr);
}

function buildChartLinkMap(report) {
  const map = {};
  const stats = Array.isArray(report?.locationStats)
    ? report.locationStats
    : [];
  stats.forEach((s) => {
    const links = Array.isArray(s?.chartLinks) ? s.chartLinks : [];
    links.forEach((l) => {
      if (l?.chart && l?.report) map[String(l.chart)] = String(l.report);
    });
  });
  return map;
}

function flattenFailures(report) {
  const out = [];
  const stats = Array.isArray(report?.locationStats)
    ? report.locationStats
    : [];
  stats.forEach((s) => {
    const loc = s?.location ?? "";
    const fails = Array.isArray(s?.failures) ? s.failures : [];
    fails.forEach((f) => out.push({ ...f, location: loc }));
  });
  return out;
}

function Default() {
  const { t } = useTranslation();
  const {
    user: { firstName },
  } = useAuth();

  const [reports, setReports] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    try {
      // pull a little extra so charts still look good if some days are missing
      const ref = database
        .ref("hydrotest/dailyReport")
        .orderByChild("timestamp")
        .limitToLast(90);

      const handleValue = (snap) => {
        const val = snap.val() || {};
        const arr = Object.entries(val)
          .map(([reportId, report]) => ({ reportId, ...(report || {}) }))
          .filter((r) => r)
          .sort((a, b) => safeNum(a.timestamp) - safeNum(b.timestamp));
        setReports(arr);
        setLoading(false);
      };

      const handleError = (err) => {
        setError(err);
        setLoading(false);
      };

      ref.on("value", handleValue, handleError);
      return () => ref.off("value", handleValue);
    } catch (e) {
      setError(e);
      setLoading(false);
      return undefined;
    }
  }, []);

  // NOTE: The daily report Cloud Function runs ~6am and includes data beginning yesterday,
  // so "today" will typically have no report. Use a 7-day running average vs overall average.
  const last7 = reports.slice(-7);
  const overall = reports;

  const last7Summary = summarizeReportsWindow(last7);
  const overallSummary = summarizeReportsWindow(overall);

  const latestReport = reports.length ? reports[reports.length - 1] : null;
  const latestFailures = latestReport
    ? flattenFailures(latestReport).slice(0, 15)
    : [];
  const chartLinkMap = latestReport ? buildChartLinkMap(latestReport) : {};

  const pctDelta = (cur, base) => {
    if (!base || base <= 0) return null;
    return (cur - base) / base;
  };

  const itemsDelta = pctDelta(last7Summary.avg.items, overallSummary.avg.items);
  const testsDelta = pctDelta(last7Summary.avg.tests, overallSummary.avg.tests);
  // For failures, lower is better — but keep delta math consistent.
  const failsDelta = pctDelta(
    last7Summary.avg.failures,
    overallSummary.avg.failures
  );
  // Pass rate: compare percentage points (7d vs overall) — weighted by items.
  const passRateDelta = last7Summary.avg.passRate - overallSummary.avg.passRate;

  const fmtPct = (p) => {
    if (p === null || p === undefined || Number.isNaN(p)) return "—";
    const sign = p >= 0 ? "+" : "";
    return `${sign}${Math.round(p * 100)}%`;
  };

  const fmtPctPoints = (p) => {
    if (p === null || p === undefined || Number.isNaN(p)) return "—";
    const sign = p >= 0 ? "+" : "";
    return `${sign}${Math.round(p * 100)} pts`;
  };

  const chip7d = "7-day avg";
  const fmtAvgInt = (n) => String(Math.round(Number(n) || 0));

  return (
    <React.Fragment>
      <Helmet title="Default Dashboard" />
      <Grid justifyContent="space-between" container spacing={6}>
        <Grid item>
          <Typography variant="h3" gutterBottom>
            Default Dashboard
          </Typography>
          <Typography variant="subtitle1">
            {t("Welcome back")}, {firstName}!{" "}
            <span role="img" aria-label="Waving Hand Sign">
              👋
            </span>
          </Typography>
        </Grid>

        <Grid item>
          <Actions />
        </Grid>
      </Grid>

      <Divider my={6} />

      {error && (
        <Typography variant="subtitle2" color="error" mb={6}>
          Failed to load hydrotest daily reports:{" "}
          {String(error?.message || error)}
        </Typography>
      )}

      <Grid container spacing={6}>
        <Grid item xs={12} sm={12} md={6} lg={3} xl>
          <Stats
            title="Items Tested (7d avg)"
            amount={loading ? "…" : fmtAvgInt(last7Summary.avg.items)}
            chip={chip7d}
            percentagetext={fmtPct(itemsDelta)}
            percentagecolor={(itemsDelta ?? 0) >= 0 ? green[500] : red[500]}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={3} xl>
          <Stats
            title="Tests Run (7d avg)"
            amount={loading ? "…" : fmtAvgInt(last7Summary.avg.tests)}
            chip={chip7d}
            percentagetext={fmtPct(testsDelta)}
            percentagecolor={(testsDelta ?? 0) >= 0 ? green[500] : red[500]}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={3} xl>
          <Stats
            title="Failures (7d avg)"
            amount={loading ? "…" : fmtAvgInt(last7Summary.avg.failures)}
            chip={chip7d}
            percentagetext={fmtPct(failsDelta)}
            // Lower failures is better
            percentagecolor={(failsDelta ?? 0) <= 0 ? green[500] : red[500]}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={3} xl>
          <Stats
            title="Pass Rate (7d avg)"
            amount={
              loading
                ? "…"
                : `${Math.round((last7Summary.avg.passRate || 0) * 100)}%`
            }
            chip={chip7d}
            percentagetext={fmtPctPoints(passRateDelta)}
            percentagecolor={(passRateDelta ?? 0) >= 0 ? green[500] : red[500]}
          />
        </Grid>
      </Grid>

      <Grid container spacing={6}>
        <Grid item xs={12} lg={8}>
          <LineChart
            reports={reports.slice(-30)}
            labelFormatter={shortDateLabel}
            // separate series by location (items tested)
          />
        </Grid>
        <Grid item xs={12} lg={4}>
          <DoughnutChart
            // Show last 7 days totals for pass/fail
            summary={{
              items: last7Summary.totals.items,
              tests: last7Summary.totals.tests,
              failures: last7Summary.totals.failures,
            }}
            dateLabel="Last 7 days"
          />
        </Grid>
      </Grid>
      <Grid container spacing={6}>
        <Grid item xs={12} lg={4}>
          <BarChart
            reports={reports.slice(-7)}
            labelFormatter={shortDateLabel}
          />
        </Grid>
        <Grid item xs={12} lg={8}>
          <Table failures={latestFailures} chartLinkMap={chartLinkMap} />
        </Grid>
      </Grid>
    </React.Fragment>
  );
}

export default Default;
